<script lang="ts">
  import { createEventDispatcher, onMount } from 'svelte';
import type {
  AdminEventConfig,
  AdminInventorySkuRecord,
  AdminInventoryCommittedEntry,
  AdminInventoryHoldEntry,
  AdminEventTicketSummary
} from '../../lib/types';
  import SurfaceCard from './ui/SurfaceCard.svelte';
  import StatusBadge from './ui/StatusBadge.svelte';
  import {
    releaseConvexReservation,
    sweepExpiredReservations,
    updateAdminTicketExtraQuantity,
    updateAdminExtraCapacity,
    addAdminTicketExtra,
    fetchAdminEventTickets
  } from '../../lib/convexApi';

  export let events: AdminEventConfig[] = [];
  export let inventory: AdminInventorySkuRecord[] | null = null;
  export let loading = false;
  export let error: string | null = null;
  export let selectedEventId: string | null = null;

  const dispatch = createEventDispatcher<{
    changeEvent: { eventId: string | null };
    refresh: void;
  }>();

  let drawerOpen = false;
  let drawerSku: string | null = null;
  let drawerRecord: AdminInventorySkuRecord | null = null;
  let editQuantities: Record<string, number> = {};
  let releasing: Record<string, boolean> = {};
  let saving: Record<string, boolean> = {};
  let releaseReasons: Record<string, string> = {};
  let capacityInput = '';
  let capacitySaving = false;
  let actionMessage: string | null = null;
  let actionError: string | null = null;
  let tickets: AdminEventTicketSummary[] = [];
  let ticketsEventId: string | null = null;
  let ticketsLoading = false;
  let ticketsError: string | null = null;
  let addTicketId = '';
  let addQuantity = 1;
  let addingExtra = false;

  $: eventOptions = events.map(event => [event.id, event.name]);

onMount(() => {
  if (selectedEventId === null && events.length > 0) {
    dispatch('changeEvent', { eventId: events[0].id });
  }
});

$: if (drawerOpen && selectedEventId) {
  void loadTicketsForEvent(selectedEventId);
}

$: if (drawerSku && inventory) {
  const match = inventory.find(item => item.sku === drawerSku);
  if (match) {
    drawerRecord = match;
    capacityInput = String(match.capacity ?? 0);
      editQuantities = {};
      for (const entry of match.committed) {
        if (entry.ticketExtraId) {
          editQuantities[entry.ticketExtraId] = entry.quantity;
        }
      }
    } else {
      drawerOpen = false;
      drawerSku = null;
      drawerRecord = null;
    }
  }

async function openDrawer(record: AdminInventorySkuRecord) {
  drawerSku = record.sku;
  drawerRecord = record;
  capacityInput = String(record.capacity ?? 0);
  const quantities: Record<string, number> = {};
  for (const entry of record.committed) {
    if (entry.ticketExtraId) {
      quantities[entry.ticketExtraId] = entry.quantity;
    }
  }
  editQuantities = quantities;
  saving = {};
  releasing = {};
  actionMessage = null;
  actionError = null;
  addTicketId = '';
  addQuantity = 1;
  ticketsError = null;
  await loadTicketsForEvent(selectedEventId);
  drawerOpen = true;
}

function closeDrawer() {
  drawerOpen = false;
  drawerSku = null;
  drawerRecord = null;
  actionMessage = null;
  actionError = null;
  tickets = [];
  ticketsEventId = null;
  ticketsLoading = false;
  ticketsError = null;
  addTicketId = '';
  addQuantity = 1;
}

function handleEventChange(event: Event) {
  const value = (event.currentTarget as HTMLSelectElement).value;
  dispatch('changeEvent', { eventId: value === 'none' ? null : value });
}

async function loadTicketsForEvent(eventId: string | null) {
  if (!eventId) {
    tickets = [];
    ticketsEventId = null;
    return;
  }
  if (ticketsEventId === eventId && tickets.length > 0) {
    return;
  }
  ticketsLoading = true;
  ticketsError = null;
  try {
    tickets = await fetchAdminEventTickets(eventId);
    ticketsEventId = eventId;
  } catch (error) {
    console.error('Failed to load tickets', error);
    ticketsError = error instanceof Error ? error.message : 'Failed to load tickets';
    tickets = [];
    ticketsEventId = null;
  } finally {
    ticketsLoading = false;
  }
}

  function setEditQuantityValue(id: string, value: number) {
    editQuantities = { ...editQuantities, [id]: value };
  }

  function setSavingFlag(id: string, value: boolean) {
    if (value) {
      saving = { ...saving, [id]: true };
    } else {
      const { [id]: _, ...rest } = saving;
      saving = rest;
    }
  }

  function setReleasingFlag(id: string, value: boolean) {
    if (value) {
      releasing = { ...releasing, [id]: true };
    } else {
      const { [id]: _, ...rest } = releasing;
      releasing = rest;
    }
  }

  function setReleaseReasonValue(id: string, value: string) {
    releaseReasons = { ...releaseReasons, [id]: value };
  }

  function isQuantityDirty(entry: AdminInventoryCommittedEntry): boolean {
    if (!entry.ticketExtraId) return false;
    return (editQuantities[entry.ticketExtraId] ?? entry.quantity) !== entry.quantity;
  }

  function increaseQuantity(id: string, current: number) {
    setEditQuantityValue(id, (editQuantities[id] ?? current) + 1);
  }

  function decreaseQuantity(id: string, current: number) {
    setEditQuantityValue(id, Math.max(0, (editQuantities[id] ?? current) - 1));
  }

  function handleQuantityInput(id: string, event: Event) {
    const target = event.currentTarget as HTMLInputElement;
    const value = Math.max(0, Number(target.value) || 0);
    setEditQuantityValue(id, value);
  }

function handleReleaseReasonInput(id: string, event: Event) {
  const target = event.currentTarget as HTMLTextAreaElement;
  setReleaseReasonValue(id, target.value);
}

function availableTicketsForDrawer(): AdminEventTicketSummary[] {
  if (!drawerRecord) return [];
  const takenIds = new Set(
    drawerRecord.committed
      .map(entry => entry.ticketId)
      .filter((id): id is string => typeof id === 'string' && id.length > 0)
  );

  return tickets.filter(ticket => !takenIds.has(ticket.ticketId));
}

async function saveQuantity(entry: AdminInventoryCommittedEntry) {
  if (!drawerRecord || !entry.ticketExtraId) return;
  const nextQuantity = Math.max(0, Math.floor(editQuantities[entry.ticketExtraId] ?? entry.quantity));
  if (nextQuantity === entry.quantity) return;

    setSavingFlag(entry.ticketExtraId, true);
    actionError = null;
    actionMessage = null;
    try {
      await updateAdminTicketExtraQuantity(entry.ticketExtraId, nextQuantity);
      actionMessage = `${drawerRecord.name}: updated quantity for ${entry.ticketCode ?? 'ticket'} to ${nextQuantity}.`;
    } catch (error) {
      console.error('Failed to update quantity', error);
      actionError = error instanceof Error ? error.message : 'Failed to update quantity';
      setEditQuantityValue(entry.ticketExtraId, entry.quantity);
    } finally {
      setSavingFlag(entry.ticketExtraId, false);
      dispatch('refresh');
    }
  }

  async function releaseHold(hold: AdminInventoryHoldEntry) {
    const reservationId = hold.reservationId;
    setReleasingFlag(reservationId, true);
    actionError = null;
    actionMessage = null;
    try {
      const reason = releaseReasons[reservationId]?.trim() || undefined;
      await releaseConvexReservation(reservationId, reason);
      actionMessage = `${drawerRecord?.name ?? 'Hold'}: released reservation ${reservationId}.`;
      setReleaseReasonValue(reservationId, '');
    } catch (error) {
      console.error('Failed to release reservation', error);
      actionError = error instanceof Error ? error.message : 'Failed to release reservation';
    } finally {
      setReleasingFlag(reservationId, false);
      dispatch('refresh');
    }
  }

  async function releaseExpiredHolds() {
    if (!selectedEventId) return;
    actionError = null;
    actionMessage = null;
    try {
      await sweepExpiredReservations(selectedEventId);
      actionMessage = `${drawerRecord?.name ?? 'Holds'}: released expired reservations.`;
    } catch (error) {
      console.error('Failed to sweep expired reservations', error);
      actionError = error instanceof Error ? error.message : 'Failed to release expired holds';
    } finally {
      dispatch('refresh');
    }
  }

async function saveCapacity() {
  if (!drawerRecord || !selectedEventId) return;
  const parsed = Number(capacityInput);
  if (!Number.isFinite(parsed)) {
    actionError = 'Capacity must be a number';
      return;
    }
    if (parsed === drawerRecord.capacity) {
      actionMessage = 'Capacity unchanged.';
      return;
    }

    capacitySaving = true;
    actionError = null;
    actionMessage = null;
    try {
      await updateAdminExtraCapacity(selectedEventId, drawerRecord.sku, parsed);
      actionMessage = `${drawerRecord.name}: capacity updated to ${parsed}.`;
      drawerRecord = { ...drawerRecord, capacity: parsed };
      capacityInput = String(parsed);
    } catch (error) {
      console.error('Failed to update capacity', error);
      actionError = error instanceof Error ? error.message : 'Failed to update capacity';
      capacityInput = String(drawerRecord.capacity ?? 0);
    } finally {
      capacitySaving = false;
      dispatch('refresh');
  }
}

async function addExtraToTicket() {
  if (!drawerRecord || !selectedEventId) return;
  const ticketId = addTicketId.trim();
  const quantity = Math.max(1, Math.floor(addQuantity));
  if (!ticketId) {
    actionError = 'Select a ticket to add the extra to.';
    return;
  }

  addingExtra = true;
  actionError = null;
  actionMessage = null;
  try {
    await addAdminTicketExtra(ticketId, {
      sku: drawerRecord.sku,
      quantity
    });
    actionMessage = `${drawerRecord.name}: added ${quantity} to ticket.`;
    addTicketId = '';
    addQuantity = 1;
    await loadTicketsForEvent(selectedEventId);
  } catch (error) {
    console.error('Failed to add ticket extra', error);
    actionError = error instanceof Error ? error.message : 'Failed to add extra';
  } finally {
    addingExtra = false;
    dispatch('refresh');
  }
}

function expiredCount(record: AdminInventorySkuRecord | null): number {
  if (!record) return 0;
  const nowTs = Date.now();
  return record.holds.filter(hold => hold.expiresAt !== null && hold.expiresAt <= nowTs).length;
}

  function formatLastActivity(value: number | null): string {
    if (!value) return '—';
    const diff = Date.now() - value;
    if (diff < 0) return 'moments ago';
    const minutes = Math.floor(diff / 60000);
    if (minutes <= 0) {
      const seconds = Math.floor(diff / 1000);
      return seconds <= 5 ? 'moments ago' : `${seconds}s ago`;
    }
    if (minutes < 60) {
      return `${minutes}m ago`;
    }
    const hours = Math.floor(minutes / 60);
    return `${hours}h ago`;
  }

  function availabilityBadge(record: AdminInventorySkuRecord): string | null {
    if (record.available <= 0) return 'depleted';
    if (record.available <= 5) return 'low';
    return null;
  }
</script>

<section class="inventory">
  <header class="inventory-header">
    <div>
      <h2>Inventory</h2>
      <p class="subtitle">Live extras availability with quick adjustments.</p>
    </div>
    <label class="event-select">
      <span>Event</span>
      <select on:change={handleEventChange}>
        <option value="none" selected={selectedEventId === null}>Select event</option>
        {#each eventOptions as [id, name]}
          <option value={id} selected={selectedEventId === id}>{name}</option>
        {/each}
      </select>
    </label>
  </header>

  {#if !selectedEventId}
    <SurfaceCard>Select an event to manage extras.</SurfaceCard>
  {:else if loading && !inventory}
    <SurfaceCard>Loading inventory…</SurfaceCard>
  {:else if error}
    <SurfaceCard><p class="error-text">{error}</p></SurfaceCard>
  {:else if inventory && inventory.length === 0}
    <SurfaceCard>No extras configured for this event.</SurfaceCard>
  {:else if inventory}
    <SurfaceCard>
      <table class="inventory-table">
        <thead>
          <tr>
            <th>SKU</th>
            <th>Capacity</th>
            <th>Sold</th>
            <th>Reserved</th>
            <th>Available</th>
            <th>Last activity</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {#each inventory as record (record.sku)}
            <tr>
              <td>
                <div class="sku-name">{record.name}</div>
                <div class="muted small">{record.sku}</div>
              </td>
              <td>{record.capacity}</td>
              <td>{record.sold}</td>
              <td>{record.reserved}</td>
              <td>
                <div class="available">
                  {record.available}
                  {#if availabilityBadge(record) === 'depleted'}
                    <span class="badge badge-alert">0 left</span>
                  {:else if availabilityBadge(record) === 'low'}
                    <span class="badge badge-warning">Low</span>
                  {/if}
                </div>
              </td>
              <td>{formatLastActivity(record.lastUpdated)}</td>
              <td>
                <button type="button" class="link" on:click={() => openDrawer(record)}>Details →</button>
              </td>
            </tr>
          {/each}
        </tbody>
      </table>
    </SurfaceCard>
  {:else}
    <SurfaceCard>Loading inventory…</SurfaceCard>
  {/if}

  {#if drawerOpen && drawerRecord}
    <aside class="drawer">
      <div class="drawer-header">
        <div>
          <h3>{drawerRecord.name}</h3>
          <p class="muted">SKU {drawerRecord.sku}</p>
        </div>
        <button type="button" class="ghost" on:click={closeDrawer}>Close</button>
      </div>

      <div class="drawer-summary">
        <StatusBadge status={drawerRecord.available <= 0 ? 'expired' : 'completed'} label={`Available ${drawerRecord.available}`} />
        <div class="metrics">
          <span>Capacity {drawerRecord.capacity}</span>
          <span>Sold {drawerRecord.sold}</span>
          <span>Reserved {drawerRecord.reserved}</span>
        </div>
      </div>

      <button
        type="button"
        class="chip-action"
        on:click={releaseExpiredHolds}
        disabled={expiredCount(drawerRecord) === 0}
      >
        Release expired holds ({expiredCount(drawerRecord)})
      </button>

      {#if actionMessage}
        <div class="message success">{actionMessage}</div>
      {/if}
      {#if actionError}
        <div class="message error">{actionError}</div>
      {/if}

      <section class="drawer-section">
        <header>
          <h4>Committed extras</h4>
          <p class="muted small">Confirmed purchases tied to tickets.</p>
        </header>
        {#if drawerRecord.committed.length === 0}
          <p class="muted">No committed extras yet.</p>
        {:else}
          <ul class="list">
            {#each drawerRecord.committed as entry (entry.ticketExtraId ?? `${entry.ticketId}-${entry.ticketCode}`)}
              {@const extraId = entry.ticketExtraId}
              {@const quantityValue = extraId ? editQuantities[extraId] ?? entry.quantity : entry.quantity}
              {@const dirty = extraId ? quantityValue !== entry.quantity : false}
              <li>
                <div class="primary-row">
                  <span>{entry.ticketCode ?? entry.ticketId ?? 'Ticket'} · {entry.personName ?? 'Guest'}</span>
                  <span class="muted small">Qty {entry.quantity}</span>
                </div>
                <div class="controls">
                  {#if extraId}
                    <div class="quantity-editor">
                      <button
                        type="button"
                        on:click={() => decreaseQuantity(extraId, entry.quantity)}
                      >
                        −
                      </button>
                      <input
                        type="number"
                        min="0"
                        value={quantityValue}
                        on:input={(event) => handleQuantityInput(extraId, event)}
                      />
                      <button
                        type="button"
                        on:click={() => increaseQuantity(extraId, entry.quantity)}
                      >
                        +
                      </button>
                    </div>
                    <button
                      type="button"
                      class="primary"
                      on:click={() => saveQuantity(entry)}
                      disabled={!dirty || saving[extraId]}
                    >
                      {saving[extraId] ? 'Saving…' : 'Save'}
                    </button>
                  {:else}
                    <span class="muted small">Legacy record</span>
                  {/if}
                </div>
              </li>
            {/each}
          </ul>
        {/if}
      </section>

      <section class="drawer-section">
        <header>
          <h4>Add extra to ticket</h4>
          <p class="muted small">Assign this extra to another attendee.</p>
        </header>
        {#if ticketsLoading}
          <p class="muted">Loading tickets…</p>
        {:else if ticketsError}
          <p class="error-text">{ticketsError}</p>
        {:else if availableTicketsForDrawer().length === 0}
          <p class="muted">All tickets already include this extra.</p>
        {:else}
          <div class="add-form">
            <label>
              <span>Ticket</span>
              <select bind:value={addTicketId}>
                <option value="">Select ticket</option>
                {#each availableTicketsForDrawer() as ticket (ticket.ticketId)}
                  <option value={ticket.ticketId}>
                    {ticket.code ?? ticket.ticketId} · {ticket.personName ?? 'Guest'}
                  </option>
                {/each}
              </select>
            </label>
            <label>
              <span>Quantity</span>
              <input type="number" min="1" bind:value={addQuantity} />
            </label>
            <button
              type="button"
              class="primary"
              on:click={addExtraToTicket}
              disabled={!addTicketId || addQuantity <= 0 || addingExtra}
            >
              {addingExtra ? 'Adding…' : 'Add extra'}
            </button>
          </div>
        {/if}
      </section>

      <section class="drawer-section">
        <header>
          <h4>Active holds</h4>
          <p class="muted small">Pending reservations from checkout flows.</p>
        </header>
        {#if drawerRecord.holds.length === 0}
          <p class="muted">No active holds right now.</p>
        {:else}
          <ul class="list">
            {#each drawerRecord.holds as hold (hold.reservationId)}
              <li>
                <div class="primary-row">
                  <span>{hold.personName ?? 'Guest'} · {hold.reservationId}</span>
                  <span class="muted small">Qty {hold.quantity}</span>
                </div>
                <div class="secondary-row">
                  {#if hold.expiresAt}
                    <span class="muted small">
                      {hold.expiresAt <= Date.now() ? 'Expired' : 'Expires'} {new Date(hold.expiresAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </span>
                  {:else}
                    <span class="muted small">No expiry</span>
                  {/if}
                </div>
                <textarea
                  class="reason"
                  rows="2"
                  placeholder="Reason (optional)"
                  value={releaseReasons[hold.reservationId] ?? ''}
                  on:input={(event) => handleReleaseReasonInput(hold.reservationId, event)}
                ></textarea>
                <button
                  type="button"
                  class="secondary"
                  on:click={() => releaseHold(hold)}
                  disabled={releasing[hold.reservationId]}
                >
                  {releasing[hold.reservationId] ? 'Releasing…' : 'Release hold'}
                </button>
              </li>
            {/each}
          </ul>
        {/if}
      </section>

      <section class="drawer-section">
        <header>
          <h4>Capacity</h4>
          <p class="muted small">Update the total number of units available.</p>
        </header>
        <div class="capacity-form">
          <input type="number" bind:value={capacityInput} min="0" />
          <button type="button" class="primary" on:click={saveCapacity} disabled={capacitySaving}>
            {capacitySaving ? 'Saving…' : 'Save capacity'}
          </button>
        </div>
      </section>
    </aside>
  {/if}
</section>

<style>
  .inventory {
    display: flex;
    flex-direction: column;
    gap: var(--space-5);
    position: relative;
  }

  .inventory-header {
    display: flex;
    justify-content: space-between;
    gap: var(--space-3);
  }

  .inventory-header h2 {
    margin: 0;
    font-size: 1.5rem;
  }

  .inventory-header .subtitle {
    margin: 0.35rem 0 0;
    color: var(--color-text-muted);
  }

  .event-select {
    display: flex;
    flex-direction: column;
    gap: 0.35rem;
    font-size: 0.9rem;
  }

  .event-select select {
    padding: 0.45rem 0.75rem;
    border-radius: var(--radius-lg);
    border: 1px solid transparent;
    background: rgba(255, 255, 255, 0.08);
    color: inherit;
  }

  .inventory-table {
    width: 100%;
    border-collapse: collapse;
  }

  .inventory-table thead th {
    text-align: left;
    padding: 0.75rem 0.5rem;
    font-size: 0.85rem;
    color: var(--color-text-muted);
    border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  }

  .inventory-table tbody td {
    padding: 0.75rem 0.5rem;
    vertical-align: middle;
    border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  }

  .sku-name {
    font-weight: 600;
  }

  .available {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .badge {
    padding: 0.2rem 0.6rem;
    border-radius: 999px;
    font-size: 0.7rem;
  }

  .badge-alert {
    background: rgba(255, 108, 108, 0.2);
    color: #ff9a9a;
  }

  .badge-warning {
    background: rgba(255, 204, 102, 0.2);
    color: #ffcc66;
  }

  .link {
    padding: 0.35rem 0.5rem;
    border: none;
    background: none;
    color: var(--color-text-primary);
    text-decoration: underline;
    cursor: pointer;
  }

  .drawer {
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    width: min(420px, 95vw);
    background: linear-gradient(180deg, rgba(16, 18, 26, 0.96), rgba(14, 16, 24, 0.9));
    border-left: 1px solid rgba(255, 255, 255, 0.1);
    padding: var(--space-5);
    display: flex;
    flex-direction: column;
    gap: var(--space-4);
    overflow-y: auto;
    z-index: 30;
  }

  .drawer-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
  }

  .drawer-header h3 {
    margin: 0;
  }

  .ghost {
    padding: 0.45rem 0.75rem;
    border-radius: var(--radius-lg);
    border: 1px solid rgba(255, 255, 255, 0.12);
    background: transparent;
    color: inherit;
    cursor: pointer;
  }

  .drawer-summary {
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
  }

  .metrics {
    display: flex;
    gap: var(--space-3);
    font-size: 0.9rem;
    color: var(--color-text-muted);
  }

  .chip-action {
    align-self: flex-start;
    padding: 0.4rem 0.75rem;
    border-radius: 999px;
    border: none;
    background: rgba(255, 255, 255, 0.08);
    color: inherit;
    cursor: pointer;
  }

  .chip-action:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }

  .message {
    padding: 0.6rem 0.75rem;
    border-radius: var(--radius-md);
    font-size: 0.85rem;
  }

  .message.success {
    background: rgba(120, 255, 186, 0.12);
    color: #8de8b8;
  }

  .message.error {
    background: rgba(255, 120, 120, 0.12);
    color: #ff9a9a;
  }

  .drawer-section {
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
  }

  .drawer-section header h4 {
    margin: 0;
  }

  .list {
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
  }

  .primary-row {
    display: flex;
    justify-content: space-between;
    font-weight: 600;
  }

  .secondary-row {
    display: flex;
    justify-content: space-between;
  }

  .controls {
    display: flex;
    gap: var(--space-3);
    align-items: center;
    margin-top: 0.5rem;
  }

  .quantity-editor {
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
  }

  .quantity-editor button {
    width: 1.75rem;
    height: 1.75rem;
    border-radius: 50%;
    border: none;
    background: rgba(255, 255, 255, 0.08);
    color: inherit;
    cursor: pointer;
  }

  .quantity-editor input {
    width: 3rem;
    text-align: center;
    border: 1px solid rgba(255, 255, 255, 0.12);
    border-radius: var(--radius-md);
    background: rgba(255, 255, 255, 0.05);
    color: inherit;
    padding: 0.3rem;
  }

  .primary,
  .secondary {
    padding: 0.45rem 0.75rem;
    border-radius: var(--radius-lg);
    border: none;
    cursor: pointer;
  }

  .primary {
    background: linear-gradient(135deg, rgba(240, 169, 74, 0.85), rgba(178, 127, 244, 0.65));
    color: #050608;
  }

  .secondary {
    background: rgba(255, 255, 255, 0.08);
    color: inherit;
  }

  .reason {
    width: 100%;
    border-radius: var(--radius-md);
    border: 1px solid rgba(255, 255, 255, 0.1);
    background: rgba(255, 255, 255, 0.05);
    color: inherit;
    padding: 0.45rem;
    margin-top: 0.5rem;
  }

  .add-form {
    display: grid;
    gap: var(--space-3);
  }

  .add-form label {
    display: flex;
    flex-direction: column;
    gap: 0.35rem;
    font-size: 0.85rem;
  }

  .add-form select,
  .add-form input[type='number'] {
    padding: 0.45rem 0.6rem;
    border-radius: var(--radius-md);
    border: 1px solid rgba(255, 255, 255, 0.12);
    background: rgba(255, 255, 255, 0.05);
    color: inherit;
  }

  .capacity-form {
    display: flex;
    gap: var(--space-3);
    align-items: center;
  }

  .capacity-form input {
    width: 5rem;
    border-radius: var(--radius-md);
    border: 1px solid rgba(255, 255, 255, 0.12);
    background: rgba(255, 255, 255, 0.05);
    color: inherit;
    padding: 0.4rem;
  }

  .error-text {
    color: #ff9a9a;
    margin: 0;
  }

  @media (max-width: 1024px) {
    .inventory-header {
      flex-direction: column;
      align-items: flex-start;
    }
  }
</style>
